export type PwaBrowserKind =
  | 'ios-safari'
  | 'ios-in-app'
  | 'ios-unsupported'
  | 'android'
  | 'standalone'
  | 'desktop';

export interface PwaEnvironment {
  browserKind: PwaBrowserKind;
  isIos: boolean;
  isSafari: boolean;
  isInAppBrowser: boolean;
  isStandalone: boolean;
  isAndroid: boolean;
}

export interface PwaEnvironmentInput {
  userAgent: string;
  vendor?: string;
  standalone?: boolean;
  displayModeStandalone?: boolean;
  maxTouchPoints?: number;
  platform?: string;
}

const IOS_IN_APP_PATTERNS = [
  /FBAN|FBAV|FBIOS|FB_IAB/i,
  /Instagram/i,
  /WhatsApp/i,
  /Line\//i,
  /Messenger/i,
  /GSA\//i,
  /CriOS\/.*GSA/i,
  /Twitter/i,
  /TikTok/i,
  /LinkedInApp/i,
];

export function getPwaEnvironment(input: PwaEnvironmentInput): PwaEnvironment {
  const userAgent = input.userAgent;
  const platform = input.platform ?? '';
  const vendor = input.vendor ?? '';
  const isIpadOsDesktopMode =
    platform === 'MacIntel' && (input.maxTouchPoints ?? 0) > 1 && /Safari/i.test(userAgent);
  const isIos = /iPad|iPhone|iPod/i.test(userAgent) || isIpadOsDesktopMode;
  const isAndroid = /Android/i.test(userAgent);
  const isInAppBrowser = IOS_IN_APP_PATTERNS.some((pattern) => pattern.test(userAgent));
  const isSafari =
    isIos &&
    /Safari/i.test(userAgent) &&
    /Apple/i.test(vendor || 'Apple') &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|YaBrowser/i.test(userAgent) &&
    !isInAppBrowser;
  const isStandalone = Boolean(input.standalone || input.displayModeStandalone);

  let browserKind: PwaBrowserKind = 'desktop';
  if (isStandalone) {
    browserKind = 'standalone';
  } else if (isIos && isInAppBrowser) {
    browserKind = 'ios-in-app';
  } else if (isIos && isSafari) {
    browserKind = 'ios-safari';
  } else if (isIos) {
    browserKind = 'ios-unsupported';
  } else if (isAndroid) {
    browserKind = 'android';
  }

  return {
    browserKind,
    isIos,
    isSafari,
    isInAppBrowser,
    isStandalone,
    isAndroid,
  };
}

export function readCurrentPwaEnvironment(): PwaEnvironment {
  const standalone = 'standalone' in window.navigator && Boolean(window.navigator.standalone);
  const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false;

  return getPwaEnvironment({
    userAgent: window.navigator.userAgent,
    vendor: window.navigator.vendor,
    standalone,
    displayModeStandalone,
    maxTouchPoints: window.navigator.maxTouchPoints,
    platform: window.navigator.platform,
  });
}
